import React from 'react';
import PropTypes from 'prop-types';
import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useNavigate } from 'react-router';
import { EDIT_PROMOTIONS } from '../../constants/routes';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import { Chip, CircularProgress, Button } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Snackbar from '@mui/material/Snackbar';
import Alert from '../Alert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import {
  LIST_TABLE_HEADERS,
  toTitleCase,
  generateHeadline,
  formatDateToMDY,
} from '../../constants/utils';
import PromotionPreviewDialog from '../PromotionPreviewDialog/PromotionPreviewDialog';
import { styles } from './style';
import BouncingLogo from '../Shimmer/BouncingLogo';
import usePromotionListing from './usePromotionListingHook';

const tooltips = {
  ARCHIVED: 'Promotion has crossed its end date.',
  FROZEN: 'Promotion has crossed its freeze date.',
};

const PromotionListingTable = ({
  defaultPromotionsData = [],
  querySet,
  isAdmin,
  totalPages,
  triggerResetSort = false,
  sortReset = () => {},
}) => {
  const {
    promotionsData,
    sortByStartDate,
    sortByEndDate,
    sortByPublishDate,
    sortByStoreName,
    showOffer,
    activeSort,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    showLoader,
    loadingDuplicate,
    recentlyAddedId,
    paperRef,

    setShowOffer,
    handleSnackbarClose,
    handleChangePage,
    handleStartDateSort,
    handleEndDateSort,
    handlePublishDateSort,
    handleStoreSort,
    handleDeletePromotion,
    handleDuplicatePromotion,

    generateHistoricHeadline,
  } = usePromotionListing(querySet, triggerResetSort, sortReset, defaultPromotionsData);

  const navigate = useNavigate();
  const AUTO_HIDE_DURATION = 3000;

  const handleOpenCreateEditScreen = (id) => {
    navigate(`${EDIT_PROMOTIONS}/${id}`);
  };

  // Function to determine action button content based on row status and admin status
  const getActionButtonContent = (row) => {
    if (!isAdmin && row?.status === 'OPEN') {
      return <EditIcon />;
    }
    return <EditIcon />;
  };

  // Function to determine action button title based on row status and admin status
  const getActionButtonTitle = (_row) => {
    return 'Edit';
  };

  // Function to determine if status has a tooltip
  const renderStatusChip = (status) => {
    if (tooltips[status]) {
      return (
        <Tooltip style={{ width: '90px' }} title={tooltips[status]} key={status}>
          <Chip sx={styles[status?.toLowerCase()]} label={toTitleCase(status)} />
        </Tooltip>
      );
    }
    return <Chip sx={styles[status?.toLowerCase()]} label={toTitleCase(status)} />;
  };

  // Function to determine headline display text
  const getHeadlineText = (row) => {
    if (row?.headline?.historic) {
      return generateHistoricHeadline(row);
    }

    if (row?.headline && !row?.headline?.historic) {
      return generateHeadline(row.headline, row.conjunction, row.flip);
    }

    return '[Please Select an HEADLINE]';
  };

  // Get date display text or dash
  const getDateDisplay = (dateString) => {
    if (dateString) {
      return formatDateToMDY(dateString);
    }
    return <span>-</span>;
  };

  // Create header cell for sortable columns
  const renderSortableHeader = (Header, accessor, sortHandler, isActive) => {
    const sortIconComponent = isActive ? (
      <ArrowDropUpIcon sx={styles.upArrowStyles} />
    ) : (
      <ArrowDropDownIcon sx={styles.downArrowStyles} />
    );

    return (
      <Button
        variant="text"
        sx={{
          ...styles.clickableHeader,
          color: '#fff',
          textTransform: 'none',
          padding: '0px',
        }}
        onClick={() => {
          switch (accessor) {
            case 'startDate':
              sortHandler(!sortByStartDate);
              break;
            case 'endDate':
              sortHandler(!sortByEndDate);
              break;
            case 'publishDate':
              sortHandler(!sortByPublishDate);
              break;
            case 'store':
              sortHandler(!sortByStoreName);
              break;
            default:
              break;
          }
        }}
      >
        {Header} {sortIconComponent}
      </Button>
    );
  };

  return (
    <Paper sx={{ margin: '30px auto', width: '100%', overflow: 'hidden' }} ref={paperRef}>
      <TableContainer component={Paper}>
        <TableMui>
          <TableHead>
            <TableRow>
              {LIST_TABLE_HEADERS.map(({ Header, accessor }) => (
                <TableCell
                  style={{
                    fontWeight: 900,
                    color: '#fff',
                    background: '#222',
                  }}
                  align={'center'}
                  key={accessor}
                >
                  {accessor === 'startDate' &&
                    renderSortableHeader(
                      Header,
                      accessor,
                      handleStartDateSort,
                      activeSort === 'startDate'
                    )}
                  {accessor === 'endDate' &&
                    renderSortableHeader(
                      Header,
                      accessor,
                      handleEndDateSort,
                      activeSort === 'endDate'
                    )}
                  {accessor === 'publishDate' &&
                    renderSortableHeader(
                      Header,
                      accessor,
                      handlePublishDateSort,
                      activeSort === 'publishDate'
                    )}
                  {accessor === 'store' &&
                    renderSortableHeader(
                      Header,
                      accessor,
                      handleStoreSort,
                      activeSort === 'storeName'
                    )}
                  {accessor === 'status' && (
                    <span style={{ display: 'flex', justifyContent: 'center' }}>{Header}</span>
                  )}
                  {!['startDate', 'endDate', 'publishDate', 'store', 'status'].includes(accessor) &&
                    Header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {showLoader ? (
              <TableRow>
                <TableCell colSpan={LIST_TABLE_HEADERS.length} sx={{ textAlign: 'center' }}>
                  <BouncingLogo height="72vh" text="Loading..." />
                </TableCell>
              </TableRow>
            ) : (
              promotionsData?.map((row, i) => {
                const headline = getHeadlineText(row);
                const rowBackgroundColor =
                  recentlyAddedId === row.promotion_id ? 'lightyellow' : 'inherit';
                const showEditAction = (!isAdmin && row?.status !== 'FROZEN') || isAdmin;

                return (
                  <TableRow
                    hover
                    key={row.promotion_id || row.id || i}
                    sx={{
                      backgroundColor: rowBackgroundColor,
                      transition: 'background-color 0.5s ease',
                    }}
                  >
                    <TableCell style={{ width: '500px' }} sx={{ textAlign: 'left' }}>
                      {headline || 'Please Select an HEADLINE'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {getDateDisplay(row?.start_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {getDateDisplay(row?.end_date)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {getDateDisplay(row?.publish_date)}
                    </TableCell>
                    <TableCell style={{ width: '200px' }} sx={{ textAlign: 'center' }}>
                      {row?.store_name}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        {renderStatusChip(row?.status)}
                      </span>
                    </TableCell>
                    <TableCell sx={{ minWidth: '200px', textAlign: 'center' }}>
                      {showEditAction && (
                        <IconButton
                          color="inherit"
                          title={getActionButtonTitle(row)}
                          onClick={() => handleOpenCreateEditScreen(row.promotion_id)}
                        >
                          {getActionButtonContent(row)}
                        </IconButton>
                      )}
                      {isAdmin && (
                        <IconButton
                          title="Delete"
                          id={`delete-promotion-${row.promotion_id}`}
                          onClick={() => handleDeletePromotion(row.promotion_id)}
                          color="inherit"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      {isAdmin && (
                        <IconButton
                          title="Duplicate"
                          id={`duplicate-promotion-${row.promotion_id}`}
                          onClick={() => handleDuplicatePromotion(row.promotion_id)}
                          color="inherit"
                          disabled={loadingDuplicate === row.promotion_id}
                        >
                          {loadingDuplicate === row.promotion_id ? (
                            <CircularProgress size={24} />
                          ) : (
                            <ContentCopyIcon />
                          )}
                        </IconButton>
                      )}
                      <IconButton
                        title="Preview"
                        id={`preview-promotion-${row.promotion_id || row.id}`}
                        onClick={() => {
                          setShowOffer(row);
                        }}
                        color="inherit"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            {!promotionsData?.length && !showLoader && (
              <TableRow sx={styles.noResultHeading}>
                <TableCell colSpan={LIST_TABLE_HEADERS.length} sx={{ textAlign: 'center' }}>
                  No Promotions found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={LIST_TABLE_HEADERS.length} sx={{ textAlign: 'right' }}>
                <Pagination
                  count={totalPages}
                  onChange={handleChangePage}
                  sx={{
                    display: 'inline-flex',
                    justifyContent: 'flex-end',
                    width: 'inherit',
                  }}
                />
              </TableCell>
            </TableRow>
          </TableFooter>
        </TableMui>
        {showOffer && (
          <PromotionPreviewDialog onClose={() => setShowOffer(null)} data={showOffer} />
        )}
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={snackbarOpen}
          autoHideDuration={AUTO_HIDE_DURATION}
          onClose={handleSnackbarClose}
        >
          <Alert severity={snackbarSeverity}>{snackbarMessage}</Alert>
        </Snackbar>
      </TableContainer>
    </Paper>
  );
};

PromotionListingTable.propTypes = {
  defaultPromotionsData: PropTypes.arrayOf(
    PropTypes.shape({
      promotion_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      headline: PropTypes.oneOfType([
        PropTypes.shape({
          DISCOUNT: PropTypes.string,
          FINANCE: PropTypes.string,
          SWEETENER: PropTypes.string,
          historic: PropTypes.bool,
        }),
        PropTypes.object,
      ]),
      conjunction: PropTypes.string,
      flip: PropTypes.bool,
      status: PropTypes.string,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      publish_date: PropTypes.string,
      store_name: PropTypes.string,
    })
  ),
  querySet: PropTypes.array,
  isAdmin: PropTypes.bool,
  totalPages: PropTypes.number,
  triggerResetSort: PropTypes.bool,
  sortReset: PropTypes.func,
};

export default PromotionListingTable;
